<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import crmAPI from '../api/crm';
import { registerCrmLocales } from '../i18n';
import PipelineSettingsModal from '../components/PipelineSettingsModal.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const i18n = useI18n();

registerCrmLocales(i18n);

const accountId = computed(() => route.params.accountId || store.getters.getCurrentAccountId);

const isLoading = ref(true);
const selectedPeriod = ref('all'); // 'today' | 'week' | 'month' | 'all'

const periods = [
  { id: 'all', label: 'Todo o Período' },
  { id: 'month', label: 'Este Mês' },
  { id: 'week', label: 'Esta Semana' },
  { id: 'today', label: 'Hoje' },
];

const overviewData = ref({
  metrics: {
    total_deals: 0,
    open_deals: 0,
    won_deals: 0,
    lost_deals: 0,
    total_value: 0,
    open_value: 0,
    won_value: 0,
    lost_value: 0,
    win_rate: 0,
    average_ticket: 0,
  },
  pipeline_stats: [],
  agent_stats: [],
  recent_deals: [],
});

const pipelines = ref([]);
const showSettingsModal = ref(false);

const fetchOverview = async () => {
  isLoading.value = true;
  try {
    const params = selectedPeriod.value !== 'all' ? { period: selectedPeriod.value } : {};
    const [overviewRes, pipelinesRes] = await Promise.all([
      crmAPI.getOverview(params),
      crmAPI.getPipelines(),
    ]);
    overviewData.value = overviewRes.data || overviewData.value;
    pipelines.value = pipelinesRes.data || [];
    store.dispatch('crm/fetchPipelines');
  } catch (err) {
    console.error('Error fetching CRM overview:', err);
  } finally {
    isLoading.value = false;
  }
};

const changePeriod = periodId => {
  selectedPeriod.value = periodId;
  fetchOverview();
};

onMounted(() => {
  fetchOverview();
});

const formatCurrency = val => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(val) || 0);
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const openPipeline = pipelineId => {
  router.push({
    name: 'crm_pipeline_view',
    params: { accountId: accountId.value, pipelineId },
  });
};

const getStatusBadge = status => {
  switch (status) {
    case 'won':
      return { label: 'Ganho', bg: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' };
    case 'lost':
      return { label: 'Perdido', bg: 'bg-rose-500/10 text-rose-500 border-rose-500/20' };
    default:
      return { label: 'Em Aberto', bg: 'bg-brand-subtle text-n-brand border-brand/20' };
  }
};
</script>

<template>
  <div class="flex-1 flex flex-col h-full overflow-y-auto bg-n-background text-n-slate-12 p-5 select-none space-y-6">
    <div class="max-w-[1600px] w-full mx-auto space-y-6">
      <!-- Top Header -->
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-n-weak">
        <div class="flex items-center gap-3">
          <router-link
            :to="{ name: 'crm_dashboard_index', params: { accountId } }"
            class="h-8 px-2.5 inline-flex items-center gap-1.5 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak rounded-lg text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer"
            title="Voltar ao Quadro Kanban"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Kanban</span>
          </router-link>

          <div>
            <h1 class="text-lg font-semibold text-n-slate-12 tracking-tight flex items-center gap-2">
              Visão Geral do CRM
            </h1>
            <p class="text-xs text-n-slate-11">
              Painel gerencial consolidado de funis, faturamento e performance comercial.
            </p>
          </div>
        </div>

        <!-- Controles: Período e Gerenciar Funis -->
        <div class="flex flex-wrap items-center gap-2">
          <!-- Filtro de Período -->
          <div class="flex items-center p-0.5 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg gap-0.5">
            <button
              v-for="p in periods"
              :key="p.id"
              type="button"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer"
              :class="
                selectedPeriod === p.id
                  ? 'bg-n-solid-active text-n-brand font-semibold shadow-xs'
                  : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'
              "
              @click="changePeriod(p.id)"
            >
              {{ p.label }}
            </button>
          </div>

          <!-- Botão Atualizar -->
          <button
            type="button"
            class="p-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 transition-all cursor-pointer shadow-xs"
            title="Recarregar Métricas"
            @click="fetchOverview"
          >
            <svg class="w-3.5 h-3.5" :class="isLoading ? 'animate-spin text-n-brand' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <!-- Botão Gerenciar Funis -->
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak rounded-lg shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
            @click="showSettingsModal = true"
          >
            <svg class="w-3.5 h-3.5 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Configurar Funis</span>
          </button>
        </div>
      </div>

      <!-- Indicador de Carregamento -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <div class="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </div>

      <template v-else>
        <!-- Top KPI Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- 1. Total de Oportunidades -->
          <div class="bg-n-surface-1 border border-n-weak rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-3">
            <div class="space-y-2">
              <div class="flex items-center justify-between text-n-slate-11">
                <span class="text-xs font-medium">Total de Oportunidades</span>
                <span class="p-1.5 rounded-lg bg-brand-subtle text-n-brand border border-brand/20">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </span>
              </div>
              <div class="flex items-baseline gap-2">
                <span class="text-2xl font-bold text-n-slate-12 font-mono">{{ overviewData.metrics.total_deals }}</span>
                <span class="text-xs text-n-slate-10">negócios</span>
              </div>
            </div>

            <!-- Proporção -->
            <div class="pt-2.5 border-t border-n-weak space-y-2">
              <div class="w-full h-1.5 bg-n-alpha-2 rounded-full overflow-hidden flex gap-0.5">
                <div
                  class="bg-brand h-full transition-all"
                  :style="{ width: `${overviewData.metrics.total_deals > 0 ? (overviewData.metrics.open_deals / overviewData.metrics.total_deals) * 100 : 0}%` }"
                  title="Abertos"
                />
                <div
                  class="bg-emerald-500 h-full transition-all"
                  :style="{ width: `${overviewData.metrics.total_deals > 0 ? (overviewData.metrics.won_deals / overviewData.metrics.total_deals) * 100 : 0}%` }"
                  title="Ganhos"
                />
                <div
                  class="bg-rose-500 h-full transition-all"
                  :style="{ width: `${overviewData.metrics.total_deals > 0 ? (overviewData.metrics.lost_deals / overviewData.metrics.total_deals) * 100 : 0}%` }"
                  title="Perdidos"
                />
              </div>
              <div class="flex items-center justify-between text-[10px] text-n-slate-10">
                <span>Aberto: <b class="text-n-slate-12">{{ overviewData.metrics.open_deals }}</b></span>
                <span>Ganho: <b class="text-emerald-500">{{ overviewData.metrics.won_deals }}</b></span>
                <span>Perdido: <b class="text-rose-500">{{ overviewData.metrics.lost_deals }}</b></span>
              </div>
            </div>
          </div>

          <!-- 2. Pipeline em Negociação -->
          <div class="bg-n-surface-1 border border-n-weak rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-3">
            <div class="space-y-2">
              <div class="flex items-center justify-between text-n-slate-11">
                <span class="text-xs font-medium">Pipeline em Aberto</span>
                <span class="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
              <div>
                <span class="text-2xl font-bold text-n-slate-12 font-mono">{{ formatCurrency(overviewData.metrics.open_value) }}</span>
              </div>
            </div>

            <div class="pt-2.5 border-t border-n-weak flex items-center justify-between text-[11px] text-n-slate-10">
              <span>Total Acumulado:</span>
              <span class="font-semibold font-mono text-n-slate-12">{{ formatCurrency(overviewData.metrics.total_value) }}</span>
            </div>
          </div>

          <!-- 3. Receita Fechada -->
          <div class="bg-n-surface-1 border border-emerald-500/30 rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-3">
            <div class="space-y-2">
              <div class="flex items-center justify-between text-n-slate-11">
                <span class="text-xs font-medium text-emerald-500">Receita Fechada</span>
                <span class="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
              <div>
                <span class="text-2xl font-bold text-emerald-500 font-mono">{{ formatCurrency(overviewData.metrics.won_value) }}</span>
              </div>
            </div>

            <div class="pt-2.5 border-t border-n-weak flex items-center justify-between text-[11px]">
              <span class="text-n-slate-10">{{ overviewData.metrics.won_deals }} negócios ganhos</span>
              <span class="text-emerald-500 font-semibold bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Faturamento</span>
            </div>
          </div>

          <!-- 4. Taxa de Conversão -->
          <div class="bg-n-surface-1 border border-n-weak rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-3">
            <div class="space-y-2">
              <div class="flex items-center justify-between text-n-slate-11">
                <span class="text-xs font-medium">Taxa de Conversão</span>
                <span class="p-1.5 rounded-lg bg-purple-500/10 text-purple-500 border border-purple-500/20">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </span>
              </div>
              <div class="flex items-baseline gap-2">
                <span class="text-2xl font-bold text-purple-500 font-mono">{{ overviewData.metrics.win_rate }}%</span>
                <span class="text-xs text-n-slate-10">taxa de sucesso</span>
              </div>
            </div>

            <div class="pt-2.5 border-t border-n-weak flex items-center justify-between text-[11px] text-n-slate-10">
              <span>Ticket Médio:</span>
              <span class="font-semibold font-mono text-purple-500">{{ formatCurrency(overviewData.metrics.average_ticket) }}</span>
            </div>
          </div>
        </div>

        <!-- Seção de Funis de Vendas -->
        <div class="space-y-3.5">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <h2 class="text-sm font-semibold text-n-slate-12">
                Funis de Vendas & Etapas
              </h2>
              <span class="px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 text-xs font-mono font-semibold border border-n-weak">
                {{ overviewData.pipeline_stats.length }}
              </span>
            </div>

            <button
              type="button"
              class="text-xs text-n-brand hover:underline font-medium flex items-center gap-1 cursor-pointer"
              @click="showSettingsModal = true"
            >
              <span>+ Configurar Funis & Etapas</span>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="pipe in overviewData.pipeline_stats"
              :key="pipe.id"
              class="bg-n-surface-1 border border-n-weak hover:border-n-weak/80 rounded-xl p-4 shadow-xs transition-all flex flex-col justify-between space-y-4 group"
            >
              <div class="space-y-3">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="font-semibold text-sm text-n-slate-12 group-hover:text-n-brand transition-colors">
                      {{ pipe.name }}
                    </h3>
                    <p class="text-xs text-n-slate-11 mt-0.5">
                      Em negociação: <b class="text-n-slate-12 font-mono">{{ formatCurrency(pipe.open_value || pipe.total_value) }}</b>
                    </p>
                  </div>

                  <span class="text-xs font-semibold font-mono px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-weak shrink-0">
                    {{ pipe.deals_count }} {{ pipe.deals_count === 1 ? 'lead' : 'leads' }}
                  </span>
                </div>

                <!-- Barra Visual das Etapas -->
                <div v-if="pipe.stages && pipe.stages.length > 0" class="space-y-2">
                  <div class="w-full h-2 bg-n-alpha-2 rounded-full overflow-hidden flex gap-0.5">
                    <div
                      v-for="st in pipe.stages"
                      :key="st.id"
                      class="h-full transition-all"
                      :style="{
                        backgroundColor: st.color || '#3b82f6',
                        width: `${pipe.deals_count > 0 ? (st.deals_count / pipe.deals_count) * 100 : (100 / pipe.stages.length)}%`,
                      }"
                      :title="`${st.name}: ${st.deals_count} leads`"
                    />
                  </div>

                  <!-- Lista de Mini Badges das Etapas -->
                  <div class="flex flex-wrap gap-1.5 pt-0.5">
                    <span
                      v-for="st in pipe.stages"
                      :key="st.id"
                      class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-n-alpha-1 border border-n-weak text-[10px] text-n-slate-11"
                    >
                      <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: st.color || '#3b82f6' }" />
                      <span class="truncate max-w-[100px]">{{ st.name }}</span>
                      <b class="text-n-slate-12 font-mono">{{ st.deals_count }}</b>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Rodapé do Card com Faturamento e Ação -->
              <div class="pt-3 border-t border-n-weak flex items-center justify-between">
                <div>
                  <span class="text-[10px] text-n-slate-10 block">Receita Ganha</span>
                  <span class="text-xs font-bold font-mono text-emerald-500">{{ formatCurrency(pipe.won_value) }}</span>
                </div>

                <button
                  type="button"
                  class="h-7 px-3 text-xs font-medium bg-brand hover:bg-brand-hover text-white rounded-lg shadow-xs transition-all flex items-center gap-1 cursor-pointer"
                  @click="openPipeline(pipe.id)"
                >
                  <span>Abrir Kanban</span>
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Grid Duplo: Performance dos Vendedores + Últimos Negócios -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <!-- Performance Comercial da Equipe (5 colunas) -->
          <div class="lg:col-span-5 bg-n-surface-1 border border-n-weak rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-4">
            <div class="flex items-center justify-between border-b border-n-weak pb-3">
              <h3 class="text-xs font-semibold text-n-slate-12 uppercase tracking-wide">
                Desempenho da Equipe
              </h3>
              <span class="text-[11px] text-n-slate-10">Por Vendedor</span>
            </div>

            <div v-if="overviewData.agent_stats.length === 0" class="py-12 text-center text-xs text-n-slate-10">
              Nenhum atendente com negócios atribuídos no período.
            </div>

            <div v-else class="space-y-2.5 overflow-y-auto max-h-[380px] pr-1">
              <div
                v-for="(ag, idx) in overviewData.agent_stats"
                :key="ag.user_id"
                class="p-2 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak flex items-center justify-between gap-3"
              >
                <div class="flex items-center gap-2.5 min-w-0">
                  <span
                    class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                    :class="
                      idx === 0
                        ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                        : (idx === 1 ? 'bg-n-alpha-3 text-n-slate-12 border border-n-weak' : 'bg-n-alpha-2 text-n-slate-10')
                    "
                  >
                    {{ idx + 1 }}
                  </span>

                  <div class="w-7 h-7 rounded-lg bg-brand-subtle text-n-brand flex items-center justify-center text-[11px] font-semibold shrink-0 uppercase">
                    {{ ag.name.substring(0, 2) }}
                  </div>

                  <div class="min-w-0">
                    <h4 class="text-xs font-medium text-n-slate-12 truncate">{{ ag.name }}</h4>
                    <p class="text-[10px] text-n-slate-10">
                      {{ ag.won_deals }} ganhos de {{ ag.total_deals }} ({{ ag.win_rate }}%)
                    </p>
                  </div>
                </div>

                <div class="text-right shrink-0">
                  <span class="text-xs font-semibold font-mono text-emerald-500 block">{{ formatCurrency(ag.won_value) }}</span>
                  <span class="text-[10px] text-n-slate-10 font-mono">{{ formatCurrency(ag.total_value) }} total</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Negócios Recentes & Atividades (7 colunas) -->
          <div class="lg:col-span-7 bg-n-surface-1 border border-n-weak rounded-xl p-4 shadow-xs flex flex-col justify-between space-y-4">
            <div class="flex items-center justify-between border-b border-n-weak pb-3">
              <h3 class="text-xs font-semibold text-n-slate-12 uppercase tracking-wide">
                Oportunidades Recentes
              </h3>
              <span class="text-[11px] text-n-slate-10">Últimos Movimentos</span>
            </div>

            <div v-if="overviewData.recent_deals.length === 0" class="py-12 text-center text-xs text-n-slate-10">
              Nenhuma oportunidade registrada recentemente.
            </div>

            <div v-else class="space-y-2 overflow-y-auto max-h-[380px] pr-1">
              <div
                v-for="d in overviewData.recent_deals"
                :key="d.id"
                class="p-2.5 px-3 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak hover:border-n-weak/80 rounded-lg transition-all flex items-center justify-between gap-3 cursor-pointer group"
                @click="openPipeline(d.pipeline_id)"
              >
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <h4 class="text-xs font-medium text-n-slate-12 truncate group-hover:text-n-brand transition-colors">
                        {{ d.contact_name || d.title }}
                      </h4>
                      <span
                        class="text-[10px] font-semibold px-1.5 py-0.2 rounded border"
                        :class="getStatusBadge(d.status).bg"
                      >
                        {{ getStatusBadge(d.status).label }}
                      </span>
                    </div>
                    <div class="flex items-center gap-1.5 text-[10px] text-n-slate-10 mt-0.5">
                      <span>{{ d.pipeline_name }}</span>
                      <span>•</span>
                      <span class="text-n-slate-11 font-medium">{{ d.stage_name }}</span>
                      <span>•</span>
                      <span>{{ formatDate(d.created_at) }}</span>
                    </div>
                  </div>
                </div>

                <div class="text-right shrink-0">
                  <span class="text-xs font-semibold font-mono text-emerald-500 block">{{ formatCurrency(d.value) }}</span>
                  <span class="text-[10px] text-n-slate-10">{{ d.user_name || 'Sem atendente' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Modal de Configuração de Funis -->
      <PipelineSettingsModal
        :show="showSettingsModal"
        :pipelines="pipelines"
        @close="showSettingsModal = false"
        @pipeline-updated="fetchOverview"
        @pipeline-created="fetchOverview"
        @pipeline-deleted="fetchOverview"
      />
    </div>
  </div>
</template>
